import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { faCheck, faHistory, faPen, faPrint, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { FilterTypeEnum } from '../../_common/enums/filter.enum';
import { ShopListResponse, ShopModel } from '../shops/shop.model';
import { ShopService } from '../shops/shop.service';
import { PrinterAddOrEditDialog, PrinterAddOrEditDialogData } from './add-or-edit-dialog/printer-add-or-edit.dialog';
import { PrinterHistoryDialog } from './printer-history-dialog/printer-history.dialog';
import { PrinterHistoryListResponse, PrinterHistoryModel } from './printer-history-dialog/printer-history.model';
import { PrinterHistoryDialogData } from './printer-history-dialog/printer-history.dialog';
import { newPrinter, PrinterListResponse, PrinterModel } from './printer.model';
import { PrinterService } from './printer.service';
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_OPTIONS } from 'src/app/_common/constants';
import { PrinterTypeEnum } from './printer.enum';
import { BrandService } from '../brands/brand.service';
import { AuthService } from 'src/app/auth/auth.service';
import { PermissionEnum } from '../users/user-permission.enum';
import { TableFiltersComponent } from 'src/app/_common/components/filters/table-filters/table-filters.component';


@Component({
  selector: 'app-printer',
  templateUrl: './printer.component.html',
  styleUrls: ['./printer.component.scss']
})
export class PrintersComponent implements OnInit, OnDestroy {

  faTrue = faCheck;
  faFalse = faTimes;
  faPrint = faPrint;
  faPen = faPen;
  faTimes = faTimes;
  faCheck = faCheck;
  faHistory = faHistory;

  pageEvent!: PageEvent;

  pageIndex: number = 0;
  pageSize: number = PAGE_SIZE_DEFAULT;
  pageSizeOptions: number[] = PAGE_SIZE_OPTIONS;

  sortDirection: SortDirection = 'asc';
  sortColumn: string = 'shopNumber';

  items: PrinterModel[] = [];
  totalRows!: number;

  brands = [];
  printerTypeDescriptions = [];
  columns: any[] = [
    {
      name: 'shopNumber',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-shopNumber'
      }
    },
    {
      name: 'shopName',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-shopName'
      }
    },
    {
      name: 'printerNo',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-printerNo'
      }
    },
    {
      name: 'printerName',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-printerName'
      }
    },
    {
      name: 'evidenceNumber',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-evidenceNumber'
      }
    },
    {
      name: 'printerIp',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-printerIp'
      }
    },
    {
      name: 'printerPort',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-printerPort'
      }
    },
    {
      name: 'printerType',
      filter: {
        type: FilterTypeEnum.Select,
        name: 'filter-printerType',
        options: [
          { name: '', value: null }
        ]
      },
    },
    {
      name: 'dateFrom',
      filter: {
        type: FilterTypeEnum.None,
        name: 'filter-dateFrom'
      }
    },
    {
      name: 'isActive',
      filter: {
        type: FilterTypeEnum.Select,
        name: 'filter-isActive',
        options: [
          { name: '', value: null },
          { name: 'common.yes', value: true },
          { name: 'common.no', value: false }
        ]
      }
    },
    {
      name: 'actions',
      filter: {
        type: FilterTypeEnum.None,
        name: 'filter-actions'
      }
    }
  ];

  displayedColumns: any[] = this.columns.map(x => x.name);
  filterColumns: any[] = this.columns.map(x => x.filter).map(x => x.name);
  filter: any = {};
  filterTypes: typeof FilterTypeEnum = FilterTypeEnum;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChildren('filterRefs') filterRefs!: QueryList<TableFiltersComponent>;

  printerAddOrEditDialogData: PrinterAddOrEditDialogData = {
    isNewPrinter: false,
    printerModel: newPrinter(),
    shopList: null
  }

  subscription: Subscription;
  routeState: any;
  data: ShopModel;
  permissionEnum = PermissionEnum;

  constructor(private printerService: PrinterService,
    private shopService: ShopService,
    public dialog: MatDialog,
    private router: Router,
    private toastr: ToastrService,
    private translateService: TranslateService,
    public brandService: BrandService,
    public authService: AuthService,
  ) {

    if (this.router.getCurrentNavigation()?.extras.state) {
      this.routeState = this.router.getCurrentNavigation()?.extras.state;
      if (this.routeState) {
        this.data = this.routeState.shop
          ? JSON.parse(this.routeState.shop)
          : null;

        if (this.data) {
          this.filter = { shopNumber: this.data.shopNumber }
        }
      }
    }

    this.subscription = this.printerService.componentMethodCalled$.subscribe(() => {
      this.addOrEditPrinterOpenDialog(null);
    });
  }

  ngOnInit(): void {
    if (this.brandService.brandList.model.length > 1) {
      this.columns.unshift({
        name: 'brands',
        filter: {
          type: FilterTypeEnum.MultipleSelect,
          name: 'filter-brands'
        }
      })
      this.displayedColumns = this.columns.map(x => x.name);
      this.filterColumns = this.columns.map(x => x.filter).map(x => x.name);
    }
    this.getBrandList();
    this.getPrinterTypeEnumDescriptions();
    this.shopService.getShops(0, 1000, 'shopNumber', 'asc', null).pipe(map((res: ShopListResponse) => {
      this.printerAddOrEditDialogData.shopList = res.model;
    })).subscribe();
    this.loadData();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onPaginateChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadData();
  }

  onSortData(event: Sort) {
    this.sortDirection = event.direction;
    this.sortColumn = event.active;
    this.pageIndex = 0;
    this.loadData();
  }

  loadData() {
    this.printerService
      .getPrinters(this.pageIndex, this.pageSize, this.sortColumn, this.sortDirection, this.filter)
      .pipe(map((res: PrinterListResponse) => {
        this.items = res.model;
        this.totalRows = res.totalRows;
      }))
      .subscribe();
  }

  filterData(filter: any) {
    this.filter = filter;
    this.pageIndex = 0;
    this.loadData();
  }

  getPrinterTypeEnumDescriptions() {

    this.printerService.getPrinterTypeList().subscribe(
      (response) => {
        this.printerTypeDescriptions = response.model.map((item) => {
          return { name: item.printerType, value: item.id as PrinterTypeEnum };
        });
        this.columns.find(column => column.name === "printerType").filter.options =
          this.columns.find(column => column.name === "printerType").filter.options.concat(this.printerTypeDescriptions);
      });
  }

  addOrEditPrinterOpenDialog(printerModel: PrinterModel | null) {
    if (printerModel) {
      this.printerAddOrEditDialogData.isNewPrinter = false;
      this.printerAddOrEditDialogData.printerModel = printerModel;
    }
    else {
      this.printerAddOrEditDialogData.isNewPrinter = true;
      this.printerAddOrEditDialogData.printerModel = newPrinter();
    }

    let dialogRef = this.dialog.open(PrinterAddOrEditDialog, {
      width: '400px',
      data: this.printerAddOrEditDialogData,
      disableClose: true,
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((data: PrinterAddOrEditDialogData) => {
      if (data) {
        this.printerService.savePrinter(data.printerModel).subscribe(() => {
          setTimeout(() => { this.loadData(); });
        });
      }
    });
  }

  onPrinterHistoryOpenDialog(id: number) {

    var data: PrinterHistoryDialogData = { printerHistoryList: [] };

    this.printerService.getPrinterHistory(id)
      .pipe(map((response: PrinterHistoryListResponse) => {
        if (response.error.message) {
          this.toastr.error(response.error.message);
          return;
        }

        if (response.model.length > 0) {
          data.printerHistoryList = response.model;

          this.dialog.open(PrinterHistoryDialog, {
            width: '800px',
            data: data,
            autoFocus: false
          });
        }
        else {
          this.toastr.info(this.translateService.instant('administration.printers.empty-printer-history'));
        }
      }))
      .subscribe();
  }

  getBrandList() {
    this.brandService.getBrandList().then(() => {
      const brandList = this.brandService.brandList.model;

      if (brandList.length) {
        this.brands = brandList.map(item => ({
          value: item.id,
          name: `<div class="brand"><img class="logo" src="data:image/png;base64,${item.logo}"/><span>${item.name}</span></div>`
        }));

        const brandColumn = this.columns.find(column => column.name === "brands");

        if (!brandColumn) {
          return;
        }

        brandColumn.filter.options = [...this.brands];        
        const brandFilter = this.filterRefs.find(f => f.column?.name === "brands");
        
        const allValues = this.brands.map(b => b.value);
        brandFilter.multipleSelectFormControl.setValue(allValues);
        this.filter["brands"] = allValues;
      }
    })
  }
}
